import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { format } from 'date-fns';

import { Title } from '~/components/Title';
import Table, { ColumnCustom } from '~/components/Table';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import Snackbar, { SeverityType } from '~/components/Snackbar';
import { useAssociations } from '~/services/hooks/useAssociations';
import { useContractors } from '~/services/hooks/useContractors';
import useSystemHistory from '~/services/hooks/useSystemHistory';
import exportCustomCSV from '~/util/exportCustomCSV';
import { Association, ContractorsResponse, SystemHistoryEntry } from '~/types';
import { FiltersContainer, NoteActions, NotesHint } from './styles';

type EntryTypeFilter = 'ALL' | 'ENTRY' | 'EXIT';

interface SystemHistoryRow extends SystemHistoryEntry {
  created_at_formatted: string;
  entry_type_label: string;
  amount_formatted: string;
  balance_formatted: string;
  note: string;
}

const NOTES_STORAGE_KEY = 'system-history-notes';
const FILES_STORAGE_KEY = 'system-history-files';

interface StoredFile {
  name: string;
  base64: string;
  type: string;
}

const toCurrency = (value?: number) => {
  if (typeof value !== 'number') {
    return '-';
  }

  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const toDate = (value: number) => {
  if (!value) {
    return '-';
  }

  return format(new Date(value), 'dd/MM/yyyy HH:mm');
};

export default function SystemHistory() {
  const [entryType, setEntryType] = useState<EntryTypeFilter>('ALL');
  const [association, setAssociation] = useState<Association | null>(null);
  const [contractor, setContractor] = useState<ContractorsResponse | null>(null);
  const [orderId, setOrderId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notesByEntryId, setNotesByEntryId] = useState<Record<string, string>>({});
  const [filesByEntryId, setFilesByEntryId] = useState<Record<string, StoredFile>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SeverityType>('success');

  const { associations, loading: loadingAssociations } = useAssociations();
  const { contractors } = useContractors();
  const { data, loading: loadingHistory, error: historyError, refetch } = useSystemHistory({
    associationId: association?.id,
    orderId: orderId || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const loading = loadingAssociations || loadingHistory || (associations.length > 0 && !association);

  useEffect(() => {
    if (!historyError) {
      return;
    }

    setSnackbarSeverity('error');
    setSnackbarMessage(historyError);
    setSnackbarOpen(true);
  }, [historyError]);

  useEffect(() => {
    const savedNotes = localStorage.getItem(NOTES_STORAGE_KEY);

    if (!savedNotes) {
      return;
    }

    try {
      setNotesByEntryId(JSON.parse(savedNotes));
    } catch (_) {
      setNotesByEntryId({});
    }
  }, []);

  useEffect(() => {
    const savedFiles = localStorage.getItem(FILES_STORAGE_KEY);

    if (!savedFiles) {
      return;
    }

    try {
      setFilesByEntryId(JSON.parse(savedFiles));
    } catch (_) {
      setFilesByEntryId({});
    }
  }, []);

  useEffect(() => {
    if (association || associations.length === 0) {
      return;
    }

    // Seleciona automaticamente a primeira empresa para já carregar histórico financeiro.
    setAssociation(associations[0]);
  }, [association, associations]);

  const rows = useMemo<SystemHistoryRow[]>(() => {
    return data.map(item => {
      const note = notesByEntryId[item.id] || '';

      return {
        ...item,
        entry_type_label: item.entry_type === 'ENTRY' ? 'Entrada' : 'Saida',
        created_at_formatted: toDate(item.created_at),
        amount_formatted: toCurrency(item.amount),
        balance_formatted: toCurrency(item.balance),
        note,
      };
    });
  }, [data, notesByEntryId]);

  const filteredRows = useMemo<SystemHistoryRow[]>(() => {
    return rows.filter(item => {
      if (entryType !== 'ALL' && item.entry_type !== entryType) {
        return false;
      }

      if (association?.id && item.association_id && item.association_id !== association.id) {
        return false;
      }

      if (contractor?.id && item.contractor_id && item.contractor_id !== contractor.id) {
        return false;
      }

      if (startDate) {
        const startDateTime = new Date(`${startDate}T00:00:00`).getTime();
        if (item.created_at < startDateTime) {
          return false;
        }
      }

      if (endDate) {
        const endDateTime = new Date(`${endDate}T23:59:59`).getTime();
        if (item.created_at > endDateTime) {
          return false;
        }
      }

      return true;
    });
  }, [rows, entryType, association?.id, contractor?.id, startDate, endDate]);

  const saveNotesToLocalStorage = (notes: Record<string, string>) => {
    localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
  };

  const saveFilesToLocalStorage = (files: Record<string, StoredFile>) => {
    try {
      localStorage.setItem(FILES_STORAGE_KEY, JSON.stringify(files));
    } catch (_) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Arquivo muito grande para salvar localmente.');
      setSnackbarOpen(true);
    }
  };

  const handleAttachFile = (entryId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const newFiles = { ...filesByEntryId, [entryId]: { name: file.name, base64, type: file.type } };
      setFilesByEntryId(newFiles);
      saveFilesToLocalStorage(newFiles);
      setSnackbarSeverity('success');
      setSnackbarMessage('Arquivo anexado com sucesso.');
      setSnackbarOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleViewFile = (entryId: string) => {
    const stored = filesByEntryId[entryId];
    if (!stored) return;
    const byteString = atob(stored.base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: stored.type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = stored.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChangeNote = (entryId: string, newValue: string) => {
    setNotesByEntryId(previousState => ({
      ...previousState,
      [entryId]: newValue,
    }));
  };

  const handleSaveNote = (entryId: string) => {
    const newNotesByEntryId = {
      ...notesByEntryId,
      [entryId]: notesByEntryId[entryId] || '',
    };

    setNotesByEntryId(newNotesByEntryId);
    saveNotesToLocalStorage(newNotesByEntryId);
    setSnackbarSeverity('success');
    setSnackbarMessage('Observacao salva com sucesso.');
    setSnackbarOpen(true);
  };

  const columns: { [key: string]: string | ColumnCustom<SystemHistoryRow> } = {
    created_at_formatted: 'Data',
    entry_type_label: 'Tipo',
    description: 'Descricao',
    amount_formatted: 'Valor',
    balance_formatted: 'Saldo',
    contractor_name: {
      title: 'Prestador',
      render: rowData => rowData.contractor_name || '-',
      customExport: rowData => rowData.contractor_name || '-',
    },
    customer_name: {
      title: 'Cliente',
      render: rowData => rowData.customer_name || '-',
      customExport: rowData => rowData.customer_name || '-',
    },
    association_name: {
      title: 'Empresa',
      render: rowData => rowData.association_name || association?.code || '-',
      customExport: rowData => rowData.association_name || association?.code || '-',
    },
    note: {
      title: 'Fatos/Observacoes',
      filtering: false,
      render: rowData => (
        <TextField
          value={rowData.note}
          onChange={({ target }) => handleChangeNote(rowData.id, target.value)}
          variant='outlined'
          size='small'
          multiline
          rows={2}
          fullWidth
        />
      ),
      customExport: rowData => notesByEntryId[rowData.id] || '',
    },
    actions: {
      title: 'Ações',
      filtering: false,
      sorting: false,
      export: false,
      render: rowData => (
        <NoteActions>
          <input
            type='file'
            style={{ display: 'none' }}
            ref={el => { fileInputRefs.current[rowData.id] = el; }}
            onChange={({ target }) => {
              if (target.files?.[0]) {
                handleAttachFile(rowData.id, target.files[0]);
                target.value = '';
              }
            }}
          />
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => handleSaveNote(rowData.id)}
          >
            Salvar
          </Button>
          <Button
            variant='outlined'
            size='small'
            onClick={() => fileInputRefs.current[rowData.id]?.click()}
          >
            Anexar
          </Button>
          {filesByEntryId[rowData.id] && (
            <Button
              variant='text'
              color='primary'
              size='small'
              title={filesByEntryId[rowData.id].name}
              onClick={() => handleViewFile(rowData.id)}
            >
              Ver arquivo
            </Button>
          )}
        </NoteActions>
      ),
    },
  };

  return (
    <>
      <Title marginBottom={20}>Checklist de Historico</Title>

      <FiltersContainer>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label='Tipo'
              value={entryType}
              onChange={({ target }) => setEntryType(target.value as EntryTypeFilter)}
              variant='outlined'
              size='small'
              select
              fullWidth
            >
              <MenuItem value='ALL'>Todos</MenuItem>
              <MenuItem value='ENTRY'>Entrada</MenuItem>
              <MenuItem value='EXIT'>Saida</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label='Data inicio'
              type='date'
              value={startDate}
              onChange={({ target }) => setStartDate(target.value)}
              variant='outlined'
              size='small'
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label='Data fim'
              type='date'
              value={endDate}
              onChange={({ target }) => setEndDate(target.value)}
              variant='outlined'
              size='small'
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label='Order ID'
              value={orderId}
              onChange={({ target }) => setOrderId(target.value)}
              variant='outlined'
              size='small'
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              id='system-history-association'
              options={associations}
              getOptionLabel={item => item.trading_name || item.company_name || item.code || item.id}
              renderInput={params => <TextField {...params} label='Empresa' variant='outlined' size='small' />}
              onChange={(_, value) => setAssociation(value)}
              value={association}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              id='system-history-contractor'
              options={contractors}
              getOptionLabel={item => item.name || item.organization || item.id}
              renderInput={params => <TextField {...params} label='Prestador' variant='outlined' size='small' />}
              onChange={(_, value) => setContractor(value)}
              value={contractor}
            />
          </Grid>
        </Grid>
      </FiltersContainer>

      <NotesHint>
        Use a coluna de observacoes para adicionar fatos complementares e clique em Salvar para persistir localmente.
      </NotesHint>

      <NoteActions>
        <Button variant='outlined' color='primary' size='small' onClick={refetch} disabled={loading}>
          Recarregar
        </Button>
      </NoteActions>

      <Table
        data={filteredRows}
        columns={columns}
        isLoading={loading}
        options={{
          showTitle: false,
          filtering: true,
          search: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          exportButton: true,
          exportCsv: (allColumns, allData) => {
            exportCustomCSV({
              csvColumns: allColumns as any,
              csvContent: allData as any,
              filenameOutput: 'checklist_historico.csv',
            });
          },
        }}
      />

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}
